/**
 * Wishlist - the shopper's saved products, built on the list the Soft card's heart keeps
 * (<product-wishlist-button>, assets/product-card.js): product HANDLES in this browser's localStorage under
 * "es-wishlist", every change announced on document as "es-wishlist:change".
 *
 *   <wishlist-count>    - the header heart's badge. Follows changes on this page and in other tabs.
 *   <wishlist-products> - the wishlist page grid (sections/main-wishlist.liquid). Each handle is rendered by Shopify
 *                         through the Section Rendering API in that product's URL context, reusing the Product Card
 *                         endpoint sections/recently-viewed-item.liquid. Browsers never execute <script> elements
 *                         inserted through innerHTML; the only scripts in a card are its JSON data islands.
 *                         Un-hearting a card removes it; a handle that no longer renders a product (deleted /
 *                         unpublished) is dropped from the list.
 *
 * Loaded once, globally, from layout/theme.liquid. Every storage access is guarded: private mode or blocked site data
 * just means an empty wishlist.
 */
const WISHLIST_KEY = 'es-wishlist';
const WISHLIST_EVENT = 'es-wishlist:change';

const WishlistStore = {
  read() {
    try {
      const value = JSON.parse(window.localStorage.getItem(WISHLIST_KEY) || '[]');
      return Array.isArray(value) ? value.filter((handle) => typeof handle === 'string' && handle.length > 0 && handle.length <= 255) : [];
    } catch (error) {
      return [];
    }
  },

  remove(handles) {
    try {
      const list = this.read().filter((handle) => !handles.includes(handle));
      window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
    } catch (error) {
      // Storage unavailable: nothing was kept to remove.
    }
    document.dispatchEvent(new CustomEvent(WISHLIST_EVENT, { detail: { handles } }));
  },
};

class WishlistCount extends HTMLElement {
  constructor() {
    super();
    this.render = this.render.bind(this);
    this.onStorage = this.onStorage.bind(this);
  }

  connectedCallback() {
    document.addEventListener(WISHLIST_EVENT, this.render);
    window.addEventListener('storage', this.onStorage);
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener(WISHLIST_EVENT, this.render);
    window.removeEventListener('storage', this.onStorage);
  }

  onStorage(event) {
    if (event.key === WISHLIST_KEY || event.key === null) this.render();
  }

  render() {
    const count = WishlistStore.read().length;
    this.textContent = count > 0 ? String(count) : '';
    this.classList.toggle('is-empty', count === 0);
  }
}

class WishlistProducts extends HTMLElement {
  constructor() {
    super();
    this.onChange = this.onChange.bind(this);
    this.controller = null;
  }

  connectedCallback() {
    const root = this.dataset.root || '/';
    this.root = root.endsWith('/') ? root : `${root}/`;
    this.limit = parseInt(this.dataset.limit, 10) || 48;
    this.list = this.querySelector('[data-wishlist-list]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.loading = this.querySelector('[data-wishlist-loading]');

    document.addEventListener(WISHLIST_EVENT, this.onChange);
    this.load();
  }

  disconnectedCallback() {
    document.removeEventListener(WISHLIST_EVENT, this.onChange);
    this.controller?.abort();
    this.controller = null;
  }

  async load() {
    // Newest first: the heart appends each saved product to the end of the list.
    const handles = WishlistStore.read().reverse().slice(0, this.limit);
    if (handles.length === 0) {
      this.showEmpty();
      return;
    }

    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    const results = await Promise.allSettled(handles.map((handle) => this.fetchCard(handle, signal)));
    if (!this.isConnected || signal.aborted) return;
    this.controller = null;

    const fragment = document.createDocumentFragment();
    const missing = [];
    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value) {
        const item = document.createElement('div');
        item.className = 'section-grid__item';
        item.dataset.handle = handles[index];
        item.innerHTML = result.value;
        fragment.append(item);
      } else if (result.status === 'fulfilled') {
        missing.push(handles[index]);
      }
    });

    this.list?.replaceChildren(fragment);
    if (missing.length > 0) WishlistStore.remove(missing);
    this.update();
  }

  fetchCard(handle, signal) {
    const url = `${this.root}products/${encodeURIComponent(handle)}?section_id=recently-viewed-item`;
    return fetch(url, { signal })
      .then((response) => (response.ok ? response.text() : ''))
      .then((text) => {
        const item = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-recently-viewed-item]');
        return item ? item.innerHTML : null;
      });
  }

  onChange() {
    if (!this.list) return;
    const saved = WishlistStore.read();
    Array.from(this.list.children).forEach((item) => {
      if (!saved.includes(item.dataset.handle)) item.remove();
    });
    this.update();
  }

  update() {
    const hasItems = Boolean(this.list && this.list.children.length > 0);
    if (this.loading) this.loading.hidden = true;
    if (this.list) this.list.hidden = !hasItems;
    if (this.empty) this.empty.hidden = hasItems;
  }

  showEmpty() {
    this.list?.replaceChildren();
    this.update();
  }
}

if (!customElements.get('wishlist-count')) {
  customElements.define('wishlist-count', WishlistCount);
}

if (!customElements.get('wishlist-products')) {
  customElements.define('wishlist-products', WishlistProducts);
}
